import React, { useState } from 'react';

import { TagModel } from 'models';

import { Button, ComponentSizes, GridColumn, KendoGrid, TabContainer, TabContent, TabNav, TabNavContainer, TabNavItem, TabNavLink, TabPane } from 'core/ui';

import { FileTaggingClient } from '../../../core/api/client';
import { HttpClient } from '../../../core/api/client/HttpClient';
import { useApiQuery } from '../../api/hooks/useApiQuery';

const fileTaggingClient = new FileTaggingClient(new HttpClient());

interface EnhancedTagEditorProps {
  tag: TagModel;
  onTagUpdate?: (tag: TagModel) => void;
  children?: React.ReactNode; // For the existing tag form
}

export function EnhancedTagEditor({ tag, onTagUpdate, children }: EnhancedTagEditorProps) {
  const [activeKey, setActiveKey] = useState('tag-details');

  // Fetch file tagging rules that use this tag
  const { data: relatedRules = [], isLoading } = useApiQuery({
    queryKey: ['fileTaggingRules', 'byTag', tag.id],
    apiFn: async () => {
      const allRules = await fileTaggingClient.getFileTaggingRules(false);
      return allRules.filter((rule) => rule.assignedTags?.some((rt) => rt.tagId === tag.id));
    },
    params: [],
    skipIfInvalidParams: true,
    skipCondition: () => !tag.id,
  });

  return (
    <div style={{ marginTop: '20px' }}>
      <TabContainer activeKey={activeKey} onSelect={(k) => setActiveKey(k || 'tag-details')}>
        <TabNavContainer>
          <TabNav variant="tabs">
            <TabNavItem>
              <TabNavLink eventKey="tag-details">Tag Details</TabNavLink>
            </TabNavItem>
            <TabNavItem>
              <TabNavLink eventKey="tagging-rules">File Tagging Rules</TabNavLink>
            </TabNavItem>
          </TabNav>
        </TabNavContainer>

        <TabContent>
          <TabPane eventKey="tag-details">
            <div style={{ padding: '20px 0' }}>{children}</div>
          </TabPane>

          <TabPane eventKey="tagging-rules">
            <div style={{ padding: '20px 0' }}>
              <h4>File Tagging Rules Using This Tag</h4>
              <p>These automated rules will apply this tag to files during processing.</p>

              {relatedRules.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  <p>No file tagging rules currently use this tag.</p>
                  <Button
                    onClick={() => {
                      // Navigate to file tagging management with this tag pre-selected
                      window.open(`/worklist-management?tab=file-tagging&preselect-tag=${tag.id}`, '_blank');
                    }}
                    themeColor="primary"
                  >
                    Create Tagging Rule
                  </Button>
                </div>
              ) : (
                <div style={{ height: '400px' }}>
                  <KendoGrid data={relatedRules}>
                    <GridColumn field="name" title="Rule Name" width="200px" />
                    <GridColumn field="description" title="Description" width="250px" />
                    <GridColumn field="executionPhase" title="Execution Phase" width="150px" />
                    <GridColumn field="priority" title="Priority" width="100px" />
                    <GridColumn
                      field="active"
                      title="Active"
                      width="100px"
                      cell={(props) => (
                        <td>
                          <span
                            style={{
                              color: props.dataItem.active ? 'green' : 'red',
                              fontWeight: 'bold',
                            }}
                          >
                            {props.dataItem.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      )}
                    />
                    <GridColumn
                      title="Actions"
                      width="150px"
                      cell={(props) => (
                        <td>
                          <Button
                            size={ComponentSizes.SMALL}
                            onClick={() => {
                              // Navigate to edit this specific rule
                              window.open(`/worklist-management?tab=file-tagging&edit-rule=${props.dataItem.id}`, '_blank');
                            }}
                          >
                            Edit Rule
                          </Button>
                        </td>
                      )}
                    />
                  </KendoGrid>
                </div>
              )}

              <div
                style={{
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                }}
              >
                <h5>How File Tagging Rules Work</h5>
                <ul style={{ fontSize: '14px', color: '#666', margin: '10px 0' }}>
                  <li>Rules automatically apply tags during file upload and DICOM processing</li>
                  <li>Use OData filters (e.g., "Modality~eq~CT") for simple conditions</li>
                  <li>Use Liquid templates for complex logic with DICOM metadata</li>
                  <li>Priority determines execution order (lower number = higher priority)</li>
                  <li>Multiple rules can apply the same tag based on different conditions</li>
                </ul>
              </div>
            </div>
          </TabPane>
        </TabContent>
      </TabContainer>
    </div>
  );
}
